import * as React from 'react';
import { useNavigate } from 'react-router';
import { SummarySheet } from './SummarySheet.tsx';
import { summaryKey } from './keys.ts';
import type { PiletApi } from '@chess/platform-web';
import { create } from 'zustand';
import { REVIEW_API, type Review } from '@chess/api-review';
import { useSelect } from '@chess/api-review/react';
import { GameBar, type Fact } from './GameBar.tsx';
import { ownBlunders, summaryOf } from './summary.ts';
import { backButton } from './back.ts';
import './game-bar.css';
import { STORE_API, type GameRecord, type GameStore } from '@chess/api-model';
import { useStoreState } from '@chess/api-model/react';
import { endingEntryPly, endingOf } from './ending.ts';
import { usePuzzleRun } from './use-puzzles.ts';

// The game bar across the top of the review: a back button to the screen the
// review was opened from (Home when there is none), who played, the result, how it
// ended and the particulars behind "More", all from the open game's stored
// record, and after the result how the reader's
// ending went (the Ending cards' case, from the store's facts), a chip that
// moves the board to the ending's first position. The record is read once per game;
// the move count and the book's name for the opening come from the session.
// While a puzzle is on the board (`puzzles.current()`) the row is the
// puzzle's banner instead, filled by the puzzles plugin through
// `review.header.puzzle`: the puzzle, not the game, is what the reader is on.
//
// The game's summary (2026-10-19): the bar's Summary button opens a sheet
// under the bar, over the three columns (`SummarySheet`), with the
// particulars and, through the slot `review.summary` (params `{ gameId }`),
// each stage's row of cards on this game, as on Home (plugin
// shared-last-game). The bar stays; the review stays mounted underneath.
// It closes with the button, Escape, a navigation from inside it (a card
// moving the board to a stage or a move: the rows call the `onLeave` they
// are given), another game, or a puzzle.

/** The sheet's element id, for the button's aria-controls. */
const SHEET_ID = 'review-game-summary';
/** The space between the bar and the sheet, in rem (the layout's row gap). */
const SHEET_GAP_REM = 0.75;

interface SheetState {
  open: boolean;
  set(open: boolean): void;
}
const useSheet = create<SheetState>((set) => ({ open: false, set: (open) => set({ open }) }));

interface RecordState {
  gameId?: string;
  record?: GameRecord;
}
const useRecord = create<RecordState>(() => ({}));

export function setup(app: PiletApi) {
  let review: Review | undefined;
  let games: GameStore | undefined;
  let loading: string | undefined;

  const load = async (gameId: string | undefined) => {
    if (gameId === loading) return;
    loading = gameId;
    if (!gameId || !games) {
      useRecord.setState({ gameId, record: undefined });
      return;
    }
    useRecord.setState({ gameId, record: undefined });
    const record = await games.record(gameId).catch(() => undefined);
    if (loading === gameId) useRecord.setState({ gameId, record });
  };

  const getApi = <T,>(name: string) => app.getApi<T>(name);

  /**
   * The open sheet, placed under the bar: fixed to the viewport from the
   * bar's bottom to the frame's padding, the bar's left and width, measured
   * on open and whenever the bar or the window moves; hidden until measured,
   * so it never shows at the wrong place.
   */
  const Sheet: React.FC<{ anchor: React.RefObject<HTMLElement | null>; gameId: string; facts: Fact[] }> = ({ anchor, gameId, facts }) => {
    const [box, setBox] = React.useState<{ top: number; left: number; width: number }>();
    const close = useSheet((s) => s.set);
    React.useLayoutEffect(() => {
      const place = () => {
        const bar = anchor.current;
        if (!bar) return;
        const r = bar.getBoundingClientRect();
        const rem = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
        setBox({ top: Math.round(r.bottom + SHEET_GAP_REM * rem), left: Math.round(r.left), width: Math.round(r.width) });
      };
      place();
      const observer = new ResizeObserver(place);
      if (anchor.current) observer.observe(anchor.current);
      window.addEventListener('resize', place);
      window.addEventListener('scroll', place, true);
      return () => {
        observer.disconnect();
        window.removeEventListener('resize', place);
        window.removeEventListener('scroll', place, true);
      };
    }, [anchor]);
    // The board's keys stay with the sheet while it covers the board (keys.ts); Escape closes it.
    React.useEffect(() => {
      const onKey = (e: KeyboardEvent) => {
        const what = summaryKey(e.key, { meta: e.metaKey, ctrl: e.ctrlKey, alt: e.altKey });
        if (!what) return;
        e.stopPropagation();
        if (what === 'close') {
          e.preventDefault();
          close(false);
        }
      };
      window.addEventListener('keydown', onKey, true);
      return () => window.removeEventListener('keydown', onKey, true);
    }, [close]);
    return (
      <SummarySheet
        id={SHEET_ID}
        facts={facts}
        style={box ? { top: box.top, left: box.left, width: box.width } : { visibility: 'hidden' }}>
        {/* The rows call `onLeave` right after each navigation they make (a
            stage, a move, by click or by key), and the sheet closes, the board
            moved under it. Not by watching clicks: closing in the click's
            capture phase removed the target before its own handler ran, so a
            chart point, which has no address to fall back on, did nothing
            (2026-10-19). */}
        <app.Extension name="review.summary" params={{ gameId, onLeave: () => close(false) }} />
      </SummarySheet>
    );
  };

  const Bar: React.FC = () => {
    const moveCount = useSelect(review, (s) => s.game?.moves.length ?? 0) ?? 0;
    const opening = useSelect(review, (s) => s.book?.reading?.opening.name);
    const blunders = useSelect(review, (s) => (s.game ? ownBlunders(s.scan, s.game.moves, s.reader) : undefined));
    const scanning = useSelect(review, (s) => !!s.scanning) ?? false;
    const gameId = useSelect(review, (s) => s.gameId);
    const reached = useSelect(review, (s) => s.game?.phases?.endgameStart !== undefined) ?? false;
    const scanned = useSelect(review, (s) => !!s.scan) ?? false;
    const { state: store } = useStoreState(getApi);
    const { record } = useRecord();
    const navigate = useNavigate();
    const puzzle = usePuzzleRun(app);
    const open = useSheet((s) => s.open);
    const setOpen = useSheet((s) => s.set);
    const barRef = React.useRef<HTMLElement>(null);
    // Another game, or a puzzle taking the bar: the sheet closes.
    React.useEffect(() => setOpen(false), [gameId, !!puzzle]);
    // Every hook is above this return, so the hook order never changes.
    if (puzzle) return <app.Extension name="review.header.puzzle" />;
    if (!record) return null;
    // The history state is read at render; see back.ts.
    const back = backButton(window.history.state, (to) => (typeof to === 'number' ? navigate(to) : navigate(to)));
    const stored = gameId ? store?.games.find((g) => g.summary.id === gameId) : undefined;
    const kind = endingOf({ reached, scanned, status: store?.status, stored });
    const ending =
      kind === 'loading' || kind === undefined
        ? kind
        : {
            case: kind,
            // The ending's first move, where a click on the Ending tab lands. See ending.ts.
            onOpen: () => {
              const ply = review ? endingEntryPly(review.state()) : undefined;
              if (ply !== undefined) review!.go(ply);
            },
          };
    const summary = summaryOf(record, moveCount, opening);
    return (
      <>
        <GameBar
          summary={summary}
          blunders={blunders}
          scanning={scanning}
          back={back}
          ending={ending}
          rootRef={barRef}
          sheet={{ open, onToggle: () => setOpen(!open), controls: SHEET_ID }}
        />
        {open && gameId && <Sheet anchor={barRef} gameId={gameId} facts={summary.facts} />}
      </>
    );
  };

  void Promise.all([app.getApi<Review>(REVIEW_API), app.getApi<GameStore>(STORE_API)]).then(([r, g]) => {
    review = r;
    games = g;
    r.subscribe((s) => void load(s.gameId));
    void load(r.state().gameId);
  });

  app.registerExtension('review.header', () => <Bar />);
  // Declared here, filled by shared-last-game: each stage's row on the game, `{ gameId }`.
}
